import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import { PASSWORD_POLICY_TEXT, getPasswordPolicyError } from '../utils/passwordPolicy';
import { detectDeviceLocale } from '../context/LocaleContext';
import BrandLogo from './BrandLogo';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const PENDING_SIGN_UP_KEY = 'eliteedge:pending-sign-up';

function trackAnalyticsEvent(event, details = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('eliteedge:analytics', {
    detail: { event, ...details },
  }));
}

const inputStyle = {
  width: '100%',
  padding: '12px',
  marginBottom: '12px',
  borderRadius: '8px',
  border: '1px solid var(--border-input)',
  background: 'var(--bg-input)',
  color: 'var(--text-primary)',
  fontSize: '14px',
  boxSizing: 'border-box',
  outline: 'none',
};

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const redirectInviteMatch = (redirect || '').match(/\/parent-invite\/([a-f0-9]+)/i);
  const parentInviteToken = searchParams.get('inviteToken') || redirectInviteMatch?.[1] || '';
  const invitedEmail = searchParams.get('invitedEmail') || '';
  const isParentInviteRegistration = !!parentInviteToken;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');
  const [representedClubName, setRepresentedClubName] = useState('');
  const [supportContactConsent, setSupportContactConsent] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthProviders, setOauthProviders] = useState({ google: true, microsoft: true });

  useEffect(() => {
    trackAnalyticsEvent('page_view', {
      page_title: document.title,
      page_location: window.location.href,
      page_path: window.location.pathname,
    });
    trackAnalyticsEvent('sign_up_start', { method: 'undecided' });
  }, []);

  useEffect(() => {
    let active = true;
    authAPI.getProviders()
      .then((res) => {
        if (!active) return;
        const data = res?.data || {};
        setOauthProviders({
          google: data.google !== false,
          microsoft: data.microsoft !== false,
        });
      })
      .catch(() => {
        if (!active) return;
        // Fail-open to avoid locking users out of OAuth on transient backend issues.
        setOauthProviders({ google: true, microsoft: true });
      });
    return () => { active = false; };
  }, []);

  const handleOAuthStart = (provider) => {
    trackAnalyticsEvent('sign_up_method_selected', { method: provider });
    try {
      sessionStorage.setItem(PENDING_SIGN_UP_KEY, provider);
    } catch {
      // OAuth remains available when browser storage is unavailable.
    }
    if (parentInviteToken) {
      sessionStorage.setItem('pendingParentInvite', JSON.stringify({ token: parentInviteToken, storedAt: Date.now() }));
    } else {
      sessionStorage.removeItem('pendingParentInvite');
      localStorage.removeItem('pendingParentInvite');
    }
    window.location.href = `${API_URL.replace(/\/api$/, '')}/api/auth/${provider}`;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirm) {
      setError('Passwords do not match.');
      trackAnalyticsEvent('sign_up_validation_error', { reason: 'password_mismatch' });
      return;
    }

    const policyError = getPasswordPolicyError(password);
    if (policyError) {
      setError(policyError);
      trackAnalyticsEvent('sign_up_validation_error', { reason: 'password_policy' });
      return;
    }

    if (!acceptedTerms || !acceptedPrivacy) {
      setError('You must accept the Terms of Service and Privacy Policy to create an account.');
      trackAnalyticsEvent('sign_up_validation_error', { reason: 'legal_acceptance' });
      return;
    }

    setLoading(true);
    trackAnalyticsEvent('sign_up_submit', { method: 'email' });
    try {
      const detectedLocale = detectDeviceLocale();
      const res = await authAPI.register({
        name,
        email,
        password,
        acceptedTerms,
        acceptedPrivacy,
        termsVersion: '2026-07-10',
        privacyVersion: '2026-07-10',
        country: detectedLocale.country,
        locale: detectedLocale.locale,
        mobilePhone: isParentInviteRegistration ? undefined : mobilePhone,
        representedClubName: isParentInviteRegistration ? undefined : representedClubName,
        supportContactConsent: isParentInviteRegistration ? undefined : supportContactConsent,
        parentInviteToken: parentInviteToken || undefined,
      });
      trackAnalyticsEvent('sign_up', { method: 'email' });
      // If we have a redirect (e.g., from invite flow), auto-login and redirect
      if (redirect && res.data?.token) {
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        window.location.href = redirect;
        return;
      }
      navigate('/login', {
        state: {
          message: 'Account created! Please check your email to verify your account. If it does not arrive within 5 minutes, check your junk or spam folder.',
        },
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
      trackAnalyticsEvent('sign_up_error', {
        method: 'email',
        reason: err.response?.status ? `http_${err.response.status}` : 'network_or_unknown',
      });
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-primary)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Inter, sans-serif',
    }}>
      <div style={{
        background: 'var(--bg-card)',
        padding: window.innerWidth < 480 ? '24px' : '40px',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '420px',
        border: '1px solid var(--border-color)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <BrandLogo height={54} showBeta />
          <p style={{ color: 'var(--text-dim)', fontSize: '13px', margin: '10px 0 0' }}>
            {isParentInviteRegistration ? 'Create your parent account' : 'Create your coaching account'}
          </p>
        </div>

        {isParentInviteRegistration && (
          <div style={{
            background: 'var(--surface-soft)',
            border: '1px solid var(--border-color)',
            color: 'var(--text-secondary)',
            padding: '10px 14px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '13px',
            lineHeight: 1.45,
          }}>
            Use the email address that received this invitation
            {invitedEmail ? <>: <strong>{invitedEmail}</strong></> : '.'}
          </div>
        )}

        {error && (
          <div style={{
            background: 'var(--surface-soft)',
            border: '1px solid var(--danger)',
            color: 'var(--danger)',
            padding: '10px 14px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '13px',
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleRegister}>
          <input
            type="text"
            aria-label="Full Name"
            placeholder="Full Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            style={inputStyle}
          />
          <input
            type="email"
            aria-label="Email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={inputStyle}
          />
          {!isParentInviteRegistration && (
            <>
              <input
                type="tel"
                aria-label="Mobile number optional"
                placeholder="Mobile number (optional)"
                value={mobilePhone}
                onChange={(e) => setMobilePhone(e.target.value)}
                autoComplete="tel"
                maxLength={32}
                style={inputStyle}
              />
              <input
                type="text"
                aria-label="Club or organisation represented optional"
                placeholder="Club or organisation you represent (optional)"
                value={representedClubName}
                onChange={(e) => setRepresentedClubName(e.target.value)}
                maxLength={160}
                style={inputStyle}
              />
              <p style={{ color: 'var(--text-dim)', fontSize: '11px', lineHeight: 1.45, margin: '-5px 0 12px' }}>
                Used only to help with onboarding. This does not create a club or affect club search or membership.
              </p>
              {mobilePhone.trim() && (
                <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '14px', lineHeight: 1.4 }}>
                  <input
                    type="checkbox"
                    checked={supportContactConsent}
                    onChange={(e) => setSupportContactConsent(e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  EliteEdge may contact me by phone about completing account setup.
                </label>
              )}
            </>
          )}
          <input
            type="password"
            aria-label="Password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            style={inputStyle}
          />
          <p style={{ color: 'var(--text-dim)', fontSize: '12px', lineHeight: '1.5', marginBottom: '14px' }}>
            By creating an account, you confirm you are authorised to use the service for your organisation and agree to the legal terms and privacy policy below.
          </p>
          <p style={{ color: 'var(--text-dim)', fontSize: '11px', margin: '-6px 0 14px 0' }}>
            {PASSWORD_POLICY_TEXT}
          </p>
          <input
            type="password"
            aria-label="Confirm Password"
            placeholder="Confirm Password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            autoComplete="new-password"
            style={{ ...inputStyle, marginBottom: '10px' }}
          />
          <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '8px' }}>
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              required
              style={{ marginRight: '8px' }}
            />
            I accept the <a href="/terms-of-service" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>Terms of Service</a>.
          </label>
          <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '20px' }}>
            <input
              type="checkbox"
              checked={acceptedPrivacy}
              onChange={(e) => setAcceptedPrivacy(e.target.checked)}
              required
              style={{ marginRight: '8px' }}
            />
            I accept the <a href="/privacy-policy" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>Privacy Policy</a>.
          </label>
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              background: loading ? 'var(--border-input)' : 'var(--accent)',
              color: loading ? 'var(--text-secondary)' : 'var(--text-on-accent)',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        {(oauthProviders.google || oauthProviders.microsoft) && (
          <>
            <div style={{ position: 'relative', textAlign: 'center', margin: '20px 0 16px' }}>
              <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px', background: 'var(--border-input)' }} />
              <span style={{ position: 'relative', background: 'var(--bg-card)', padding: '0 12px', color: 'var(--text-dim)', fontSize: '12px' }}>
                or continue with
              </span>
            </div>

            {oauthProviders.google && (
              <button
                onClick={() => handleOAuthStart('google')}
                style={{
                  width: '100%', padding: '12px', background: 'var(--bg-input)', color: 'var(--text-primary)',
                  border: '1px solid var(--border-input)', borderRadius: '8px', fontSize: '14px',
                  fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '10px'
                }}>
                <svg aria-hidden="true" width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                Sign up with Google
              </button>
            )}
            {oauthProviders.microsoft && (
              <button
                onClick={() => handleOAuthStart('microsoft')}
                style={{
                  width: '100%', padding: '12px', background: 'var(--bg-input)', color: 'var(--text-primary)',
                  border: '1px solid var(--border-input)', borderRadius: '8px', fontSize: '14px',
                  fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '10px', marginTop: oauthProviders.google ? '8px' : 0
                }}>
                <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 23 23">
                  <path fill="#f35325" d="M1 1h10v10H1z"/>
                  <path fill="#81bc06" d="M12 1h10v10H12z"/>
                  <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                  <path fill="#ffba08" d="M12 12h10v10H12z"/>
                </svg>
                Sign up with Microsoft
              </button>
            )}
          </>
        )}

        <p style={{ color: 'var(--text-dim)', fontSize: '13px', textAlign: 'center', marginTop: '20px' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: 'var(--accent)', textDecoration: 'none' }}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}
